'use client';

/**
 * /p/[slug] — project Overview tab. Chrome (header + tabs) lives in layout.tsx.
 * Shows live KPIs only — no duplicate module cards; the tools are the tabs.
 */
import { use, useEffect, useState } from 'react';
import Link from 'next/link';
import { notFound, redirect } from 'next/navigation';
import {
  Filter, Users, Mail, TrendingUp, CalendarCheck, Eye, DollarSign, Loader2,
} from 'lucide-react';
import { getBusinessProject, isPlaceholderCollab, hasProjectList } from '@/lib/business-projects';

interface HubSite { slug: string; domain: string; crmEnabled: boolean }
interface ProjCount { project: string; n: number; unread: number }

export default function ProjectOverviewPage({ params }: { params: Promise<{ slug: string }> }) {
  const { slug } = use(params);
  const project = getBusinessProject(slug);
  const [overview, setOverview] = useState<any>(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [sites, setSites] = useState<HubSite[]>([]);
  const [mail, setMail] = useState<{ n: number; unread: number } | null>(null);
  const [collabProjects, setCollabProjects] = useState<any[]>([]);

  useEffect(() => {
    if (!project) return;
    if (hasProjectList(slug)) {
      fetch('/api/projects').then(r => r.json())
        .then(d => setCollabProjects((d?.projects || []).filter((p: any) => p.collab === slug)))
        .catch(() => {});
    }
    if (project.funnelId) {
      setFunnelLoading(true);
      fetch(`/api/funnels/${project.funnelId}/stats?days=30`)
        .then(r => r.json())
        .then(d => setOverview(d?.stats?.overview || null))
        .catch(() => {})
        .finally(() => setFunnelLoading(false));
    }
    if (project.sites === 'factory') {
      fetch('/api/hub/sites').then(r => r.json())
        .then(d => setSites(Array.isArray(d?.sites) ? d.sites : []))
        .catch(() => {});
    }
    fetch('/api/email/inbox/projects').then(r => r.json())
      .then((rows: ProjCount[]) => {
        if (!Array.isArray(rows)) return;
        const mine = rows.filter(r =>
          project.emailKeys === 'factory'
            ? !['tls', 'olga', 'anna', 'astroforyou', 'vctronics', 'aviationprivee'].includes(r.project)
            : (project.emailKeys as string[]).includes(r.project));
        setMail({ n: mine.reduce((s, r) => s + r.n, 0), unread: mine.reduce((s, r) => s + (r.unread || 0), 0) });
      })
      .catch(() => {});
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!project) return notFound();
  // wtf lands straight on Performance — "all the websites, that's the most important"
  // collab pages keep their Overview (Projects list lives here) — no auto-redirect
  if (project.defaultTab && !hasProjectList(slug)) redirect(`/p/${slug}/${project.defaultTab}`);
  const siteList = project.sites === 'factory' ? sites : project.sites;

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      {/* Funnel KPIs */}
      {project.funnelId && (
        <section>
          <h2 className="text-sm font-medium text-gray-400 mb-3">Funnel — last 30 days</h2>
          {funnelLoading ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading funnel stats…</div>
          ) : overview ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: 'Visitors', value: overview.conversion?.startVisitors ?? '—', icon: TrendingUp },
                { label: 'Leads', value: overview.leads?.last30Days ?? overview.leads?.total ?? '—', icon: Users },
                { label: 'Booked', value: overview.appointments?.booked ?? '—', icon: CalendarCheck },
                { label: 'Video views', value: overview.videoViews?.last30Days ?? '—', icon: Eye },
                { label: 'Lead rate', value: overview.conversion?.visitorToLeadRate != null ? `${overview.conversion.visitorToLeadRate}%` : '—', icon: Filter },
                { label: 'Revenue', value: overview.revenue?.gross != null ? `$${overview.revenue.gross}` : '—', icon: DollarSign },
              ].map(({ label, value, icon: Icon }) => (
                <div key={label} className="rounded-xl border border-gray-800 bg-gray-900/60 p-4">
                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><Icon className="w-3.5 h-3.5" />{label}</div>
                  <div className="text-2xl font-semibold text-white">{value}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500">Funnel stats unreachable.</div>
          )}
        </section>
      )}
      {!project.funnelId && project.tabs.includes('funnel') && (
        <div className="rounded-xl border border-dashed border-gray-700 p-4 text-sm text-gray-400">
          Funnel exists but its stats API isn&apos;t registered in Beast yet — say the word and it gets wired like Olga&apos;s.
        </div>
      )}

      {/* Email summary */}
      {project.tabs.includes('email') && (
        <section>
          <h2 className="text-sm font-medium text-gray-400 mb-3">Email</h2>
          <Link href={`/p/${slug}/email${Array.isArray(project.emailKeys) && project.emailKeys[0] ? `?project=${project.emailKeys[0]}` : ''}`}
            className="inline-flex items-center gap-3 rounded-xl border border-gray-800 bg-gray-900/60 px-5 py-4 hover:border-gray-600 transition">
            <Mail className="w-5 h-5 text-blue-400" />
            <span className="text-xl font-semibold text-white">{mail ? mail.n : '—'}</span>
            <span className="text-sm text-gray-500">messages{mail && mail.unread > 0 ? ` · ${mail.unread} unread` : ''}</span>
          </Link>
        </section>
      )}

      {/* Collab-assigned projects (DB, projects.collab) */}
      {hasProjectList(slug) && (
        <section>
          <h2 className="text-sm font-medium text-gray-400 mb-3">Projects {collabProjects.length ? `(${collabProjects.length})` : ''}</h2>
          {collabProjects.length ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {collabProjects.map(p => (
                <Link key={p.slug} href={`/projects/${p.slug}`}
                  className="rounded-lg border border-gray-800 bg-gray-900/60 px-3 py-2 flex items-center gap-2 hover:border-gray-600 transition">
                  {p.icon && <span>{p.icon}</span>}
                  <span className="text-sm text-gray-300 truncate">{p.name}</span>
                  <span className="text-[11px] text-gray-500 ml-auto flex-shrink-0">{p.status}</span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500">No projects assigned yet — use “New Project” in the header.</div>
          )}
        </section>
      )}

      {/* Websites */}
      {project.tabs.includes('websites') && (
        <section>
          <h2 className="text-sm font-medium text-gray-400 mb-3">Websites {siteList.length ? `(${siteList.length})` : ''}</h2>
          {siteList.length ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {siteList.map(s => (
                <div key={s.slug} className="rounded-lg border border-gray-800 bg-gray-900/60 px-3 py-2 flex items-center justify-between">
                  <a href={`https://${s.domain}`} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-300 hover:text-white truncate">{s.domain}</a>
                  <Link href={`${project.tabs.includes('improve') ? `/p/${slug}/improve` : '/improve'}?site=${s.slug}`} className="text-[11px] text-gray-500 hover:text-blue-300 flex-shrink-0 ml-2">improve</Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500">No sites yet.</div>
          )}
        </section>
      )}
    </main>
  );
}
